// Team → Support access: the customer side of the lockbox. Admins see every
// grant ever issued, revoke active ones, and create new ones scoped to the
// organization, a project, a document, or one of their own AI conversations,
// expiring after 48 h by default. Without an active grant, fast.legal staff
// have no in-product path to this organization's content.
import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import { useToast } from './Toast';
import { useAuth } from '../AuthContext';
import { useCollection } from '../data/firestore';
import { useVisibleDeals } from '../data/permissions';
import {
  GRANT_LEVEL_LABEL, GRANT_DURATIONS, grantIsActive, createAccessGrant, revokeAccessGrant,
} from '../data/supportAccess';

const fmtWhen = (at) => (at ? new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function GrantModal({ orgId, role, onClose, onCreated }) {
  const { user } = useAuth();
  const toast = useToast();
  const { deals } = useVisibleDeals();
  const [level, setLevel] = useState('project');
  const [dealId, setDealId] = useState('');
  const [docId, setDocId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [duration, setDuration] = useState('48h');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const { docs: dealDocs } = useCollection(dealId && level === 'document' ? `orgs/${orgId}/deals/${dealId}/docs` : null, { orderBy: 'createdAt' });
  const { docs: sessions } = useCollection(level === 'conversation' ? 'aiSessions' : null, { where: ['createdBy', '==', user?.uid || '-'] });
  const isOwner = role === 'owner';

  const scopeLabel = () => {
    if (level === 'org') return 'Whole organization';
    if (level === 'project') return (deals || []).find((d) => d.id === dealId)?.name || '';
    if (level === 'document') return (dealDocs || []).find((d) => d.id === docId)?.title || '';
    return (sessions || []).find((s) => s.id === sessionId)?.title || 'Conversation';
  };
  const valid = reason.trim().length >= 5
    && (level === 'org' || (level === 'project' && dealId) || (level === 'document' && dealId && docId) || (level === 'conversation' && sessionId))
    && (duration !== 'never' || isOwner);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await createAccessGrant(orgId, user, {
        level,
        dealId: level === 'project' || level === 'document' ? dealId : null,
        docId: level === 'document' ? docId : null,
        sessionId: level === 'conversation' ? sessionId : null,
        label: scopeLabel(),
        reason,
        durationMs: GRANT_DURATIONS.find((d) => d.id === duration)?.ms ?? null,
      });
      toast('Support access granted', 'ok');
      onCreated?.();
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Grant support access" onClose={onClose} maxWidth={560}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <p className="cs-help" style={{ margin: 0 }}>
          fast.legal support will be able to read — not change — exactly what you select, until the grant expires or you revoke it. Every view is recorded under Activity → Operator access.
        </p>
        <div className="cs-field">
          <label htmlFor="sa-level">What they may see</label>
          <select id="sa-level" className="cs-input" value={level} onChange={(e) => { setLevel(e.target.value); setDocId(''); }}>
            {Object.entries(GRANT_LEVEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {(level === 'project' || level === 'document') && (
          <div className="cs-field">
            <label htmlFor="sa-deal">Project</label>
            <select id="sa-deal" className="cs-input" value={dealId} onChange={(e) => { setDealId(e.target.value); setDocId(''); }}>
              <option value="">Choose a project…</option>
              {(deals || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
        {level === 'document' && dealId && (
          <div className="cs-field">
            <label htmlFor="sa-doc">Document</label>
            <select id="sa-doc" className="cs-input" value={docId} onChange={(e) => setDocId(e.target.value)}>
              <option value="">Choose a document…</option>
              {(dealDocs || []).map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
        )}
        {level === 'conversation' && (
          <div className="cs-field">
            <label htmlFor="sa-session">Your AI conversation</label>
            <select id="sa-session" className="cs-input" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              <option value="">Choose a conversation…</option>
              {(sessions || []).slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)).map((s) => <option key={s.id} value={s.id}>{s.title || s.id}</option>)}
            </select>
            <span className="cs-help">Only your own conversations can be shared; each member shares theirs.</span>
          </div>
        )}
        <div className="cs-field">
          <label htmlFor="sa-duration">For how long</label>
          <select id="sa-duration" className="cs-input" value={duration} onChange={(e) => setDuration(e.target.value)}>
            {GRANT_DURATIONS.map((d) => <option key={d.id} value={d.id} disabled={d.ms == null && !isOwner}>{d.label}</option>)}
          </select>
          {duration === 'never' && (
            <span className="cs-help" style={{ color: 'var(--danger)' }}>
              Standing access stays open until someone revokes it. It is listed at the top of this section and in Activity while it lasts.
            </span>
          )}
        </div>
        <div className="cs-field">
          <label htmlFor="sa-reason">Why (shown in the log)</label>
          <textarea id="sa-reason" className="cs-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Support ticket: the redline on Section 7 renders blank" />
        </div>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={!valid || busy}>{busy ? 'Granting…' : 'Grant access'}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function SupportAccessCard({ orgId, role }) {
  const { user } = useAuth();
  const toast = useToast();
  const [showGrant, setShowGrant] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const { docs: grants } = useCollection(orgId ? `orgs/${orgId}/accessGrants` : null, { orderBy: 'createdAt', enabled: !!orgId });
  const now = Date.now();
  const active = useMemo(() => (grants || []).filter((g) => grantIsActive(g, now)), [grants, now]);
  const past = useMemo(() => (grants || []).filter((g) => !grantIsActive(g, now)), [grants, now]);

  const revoke = async (g) => {
    try {
      await revokeAccessGrant(orgId, g.id, user);
      toast('Support access revoked', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const scope = (g) => (g.level === 'org' ? 'Whole organization' : `${GRANT_LEVEL_LABEL[g.level] || g.level}: ${g.label || g.docId || g.dealId || g.sessionId}`);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)', alignItems: 'flex-start' }}>
        <span className="u-stack" style={{ '--gap': '2px', flex: 1, minWidth: 0 }}>
          <strong>fast.legal staff cannot see this organization's content.</strong>
          <span className="u-small u-muted">
            To let support look at something — for a ticket, a stuck job, a rendering problem — grant read-only access to exactly that scope for a limited time. You can revoke at any moment; every view is logged under Activity → Operator access.
          </span>
        </span>
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowGrant(true)}>Grant access</button>
      </div>
      {grants === null && <div className="cs-skeleton" style={{ height: 48 }} />}
      {grants !== null && !active.length && <div className="cs-empty">No active support access.</div>}
      {active.map((g) => (
        <div key={g.id} className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <span className="u-stack" style={{ '--gap': '2px', flex: 1, minWidth: 0 }}>
            <span className="u-small" style={{ fontWeight: 700 }}>{scope(g)}</span>
            <span className="u-xs u-muted">
              {g.grantedTo} · by {g.grantedByName || g.grantedByEmail} · {fmtWhen(g.createdAt)}
              {' · '}{g.expiresAt ? `expires ${fmtWhen(g.expiresAt)}` : 'until revoked'}
              {g.useCount ? ` · used ${g.useCount}× (last ${fmtWhen(g.lastUsedAt)})` : ' · not used yet'}
            </span>
            <span className="u-xs u-muted">{g.reason}</span>
          </span>
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
            {g.expiresAt == null && <span className="cs-badge b-warn">standing</span>}
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => revoke(g)}>Revoke</button>
          </span>
        </div>
      ))}
      {past.length > 0 && (
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setShowPast((v) => !v)}>
          {showPast ? 'Hide' : 'Show'} {past.length} expired or revoked grant{past.length === 1 ? '' : 's'}
        </button>
      )}
      {showPast && past.map((g) => (
        <div key={g.id} className="cs-card" style={{ padding: 'var(--sp-2) var(--sp-4)', opacity: 0.75 }}>
          <span className="u-xs u-muted">
            {scope(g)} · by {g.grantedByName || g.grantedByEmail} · {fmtWhen(g.createdAt)} · {g.revokedAt ? `revoked ${fmtWhen(g.revokedAt)}` : `expired ${fmtWhen(g.expiresAt)}`}{g.useCount ? ` · used ${g.useCount}×` : ' · never used'}
          </span>
        </div>
      ))}
      {showGrant && <GrantModal orgId={orgId} role={role} onClose={() => setShowGrant(false)} />}
    </div>
  );
}
